
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import AdminsAdvancedSearch from '@/components/search/AdminsAdvancedSearch.vue';

  export default defineComponent({
    name: 'admins-listing',
    components: {
      Datatable,
      AdminsAdvancedSearch,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('adminUsersListing'), [
          translate('members'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_ADMINS_STORE);
      });

      await store.dispatch(Actions.GET_ADMIN_USERS);
      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('name'),
          key: 'name',
          sortable: true,
        },
        {
          name: translate('email'),
          key: 'email',
          sortable: true,
        },
        {
          name: translate('phoneNumber'),
          key: 'phone',
          sortable: false,
        },
        {
          name: translate('roleName'),
          key: 'roleName',
          sortable: false,
        },
        {
          name: translate('status'),
          key: 'isActive',
          sortable: false,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);
      const toggleStatusUser = async (admin) => {
        const deletedMessage = admin.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_ADMIN_ACCOUNT, admin.id);
          await store.dispatch(Actions.GET_ADMIN_USERS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = admin.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const editUser = (id) => {
        router.push({ name: 'admins-editing', params: { id } });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_ADMIN_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_ADMIN_USERS);
        loading.value = false;
      };

      const onFilterChange = async (filterValues) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_ADMIN_FILTER, {
          roleId: filterValues.roleId,
          email: filterValues.email,
        });
        await store.dispatch(Actions.GET_ADMIN_USERS);
        loading.value = false;
      };

      const onResetFilters = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_ADMIN_FILTER, value);
        await store.dispatch(Actions.GET_ADMIN_USERS);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.adminsList),
        tableHeader,
        toggleStatusUser,
        translate,
        totalItems: computed(() => store.getters.listCount),
        pageChanged,
        goTo,
        editUser,
        loading,
        can,
        onFilterChange,
        onResetFilters,
      };
    },
  });
